// Список моїх битв + знайомство з фічею для нових юзерів.
import { Link } from 'react-router-dom';
import { api, type MatchListItem } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { IconTile, TabGlyph } from '../components/AppIcons';

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  WAITING:   { text: 'Очікує суперника', cls: 'text-tg-hint' },
  ACTIVE:    { text: 'У грі', cls: 'text-tg-link' },
  FINISHED:  { text: 'Завершена', cls: 'text-tg-text' },
  CANCELLED: { text: 'Скасована', cls: 'text-tg-destructive' },
};

export function Battles() {
  const { data, loading, error, reload } = useFetch(() => api.battles.myMatches(), []);
  const isEmpty = data && data.length === 0;

  return (
    <>
      <PageHeader title="Битви" subtitle="1 на 1 з другом онлайн" backTo="/" />
      <div className="space-y-3 p-4">
        {/* Hero — для нових юзерів він пояснює фічу, для досвідчених просто гарний CTA */}
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-tg-button/15 via-tg-secondary-bg to-tg-secondary-bg p-4">
          <div className="absolute -right-3 -top-3 opacity-10">
            <TabGlyph kind="battle" className="h-24 w-24" />
          </div>
          <div className="relative">
            <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-tg-button/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-tg-button">
              <span>Реальний час · 1 на 1</span>
            </div>
            <h2 className="text-base font-bold leading-tight">Виклик другу — хто кращий знавець ПДР?</h2>
            <p className="mt-1.5 text-xs text-tg-hint leading-relaxed">
              Створи битву → надішли посилання другу в Telegram → грайте <strong className="text-tg-text">синхронно онлайн</strong>:
              ті самі питання, бачите прогрес одне одного, хто більше правильних — перемагає.
            </p>
            <Link to="/battles/new" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-tg-button px-4 py-2.5 font-semibold text-tg-button-text">
              <TabGlyph kind="battle" className="h-4 w-4" /> Створити битву
            </Link>
          </div>
        </section>

        {loading && <Loader />}
        {error && <ErrorView error={error} onRetry={reload} />}

        {isEmpty && (
          <>
            {/* Розгорнутий пояснювальний блок коли ще нема жодної битви */}
            <section className="card space-y-3">
              <h3 className="text-sm font-semibold">Як працює дуель?</h3>
              <EmptyStep n={1} title="Налаштуй параметри" desc="Категорія (A, B, C...), і скільки питань — 5, 10, 15 або 20." />
              <EmptyStep n={2} title="Поділись посиланням" desc="Бот сам згенерує invite-посилання — кидай у чат чи в особисті повідомлення другу." />
              <EmptyStep n={3} title="Друг приєднується одним кліком" desc="Він натискає посилання → відкривається бот → «Прийняти виклик» → одразу старт." />
              <EmptyStep n={4} title="Грайте онлайн" desc="Бачите прогрес одне одного в реальному часі. Хто більше правильних — переможець." last />
            </section>

            <div className="grid grid-cols-2 gap-2">
              <FeatTile icon="flash" title="Швидко" desc="Дуель за 3–7 хв" />
              <FeatTile icon="target" title="Чесно" desc="Однакові питання" />
              <FeatTile icon="trophy" title="Рейтинг" desc="Перемоги в статистику" />
              <FeatTile icon="mistakes" title="Реванш" desc="Один клік на ще одну гру" />
            </div>

            <p className="text-center text-xs text-tg-hint">
              Битви — найкращий спосіб готуватись разом. Хто з друзів швидше зрозумів пріоритет проїзду перехресть?
            </p>
          </>
        )}

        {data && data.length > 0 && (
          <>
            <h2 className="px-1 pt-2 text-xs font-semibold uppercase tracking-wider text-tg-hint">
              Мої битви
            </h2>
            {data.map((m) => <BattleCard key={m.code} m={m} />)}
          </>
        )}
      </div>
    </>
  );
}

function EmptyStep({
  n,
  title,
  desc,
  last,
}: {
  n: number;
  title: string;
  desc: string;
  last?: boolean;
}) {
  return (
    <div className={`flex gap-3 ${last ? '' : 'border-b border-tg-bg/40 pb-3'}`}>
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tg-button text-xs font-bold text-tg-button-text">
        {n}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium leading-tight">{title}</div>
        <div className="mt-0.5 text-xs text-tg-hint leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function FeatTile({
  icon,
  title,
  desc,
}: {
  icon: Parameters<typeof IconTile>[0]['kind'];
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-lg bg-tg-secondary-bg p-3">
      <IconTile kind={icon} className="mb-1.5 h-8 w-8 rounded-lg" glyphClass="h-[18px] w-[18px]" />
      <div className="text-sm font-semibold leading-tight">{title}</div>
      <div className="mt-0.5 text-[11px] text-tg-hint">{desc}</div>
    </div>
  );
}

function BattleCard({ m }: { m: MatchListItem }) {
  const status = STATUS_LABEL[m.status] ?? { text: m.status, cls: '' };
  const result =
    m.status === 'FINISHED'
      ? m.isDraw
        ? '🤝 Нічия'
        : m.isWinner
          ? '🏆 Перемога'
          : '💀 Поразка'
      : null;

  return (
    <Link to={`/battles/${m.code}`} className="card flex items-center gap-3 hover:bg-tg-bg/40">
      {m.opponent?.photoUrl ? (
        <img src={m.opponent.photoUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="grid h-10 w-10 place-items-center rounded-full bg-tg-bg text-tg-hint">
          <TabGlyph kind="profile" className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">
          {m.opponent ? (m.opponent.firstName ?? m.opponent.username ?? 'Суперник') : 'Чекає на гравця'}
        </div>
        <div className="text-xs text-tg-hint">
          <span className={status.cls}>{status.text}</span>
          {m.category && <span> · {m.category.nameUk}</span>}
          <span> · {m.questionCount} пит.</span>
        </div>
      </div>
      <div className="text-right">
        {m.status === 'FINISHED' && (
          <>
            <div className="text-sm font-semibold tabular-nums">
              {m.myScore} : {m.opponentScore ?? '—'}
            </div>
            <div className="text-xs text-tg-hint">{result}</div>
          </>
        )}
        {m.status !== 'FINISHED' && <span className="text-tg-hint">›</span>}
      </div>
    </Link>
  );
}
